/**
 * Simple inline-SVG line chart for daily time-series. Takes one or more
 * named series sharing the same day-indexed x-axis. Used by the Journey
 * page for vocabulary growth, engagement rate, native ratio.
 */
type Series = { label: string; values: number[]; color: string };

export default function TimeSeriesLine({
  title,
  series,
  yMax,
  yLabel,
  yFormat = (v) => v.toFixed(0),
}: {
  title: string;
  series: Series[];
  yMax?: number;
  yLabel?: string;
  yFormat?: (v: number) => string;
}) {
  const W = 640;
  const H = 220;
  const PAD = { t: 20, r: 20, b: 30, l: 44 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const n = series.length > 0 ? Math.max(...series.map((s) => s.values.length)) : 0;

  if (n === 0) {
    return (
      <div className="card">
        <div className="mb-3">
          <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">{title}</div>
          {yLabel ? <div className="text-[11px] text-ink-faint mt-0.5">{yLabel}</div> : null}
        </div>
        <div className="text-ink-faint text-sm py-8 text-center">no data yet</div>
      </div>
    );
  }

  const rawTop = Math.max(
    ...series.flatMap((s) => s.values),
    yMax ?? 0,
  );
  const top = rawTop > 0 ? rawTop : 1;
  const xStep = n > 1 ? innerW / (n - 1) : innerW;

  const gridYs = [0, 1, 2, 3, 4].map((i) => PAD.t + (innerH * i) / 4);

  return (
    <div className="card">
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">{title}</div>
          {yLabel ? <div className="text-[11px] text-ink-faint mt-0.5">{yLabel}</div> : null}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <g stroke="#e4e1db" strokeWidth="1">
          {gridYs.map((y, i) => (
            <line key={i} x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} />
          ))}
        </g>
        <g fill="#6d6a63" fontSize="10" textAnchor="end">
          {gridYs.map((y, i) => {
            const val = top * (1 - i / 4);
            return (
              <text key={i} x={PAD.l - 4} y={y + 3}>
                {yFormat(val)}
              </text>
            );
          })}
        </g>
        {series.map((s) => {
          if (s.values.length === 0) return null;
          const pts = s.values
            .map((v, i) => {
              const x = PAD.l + (n > 1 ? i * xStep : innerW / 2);
              const y = PAD.t + innerH * (1 - (top ? v / top : 0));
              return `${x.toFixed(1)},${y.toFixed(1)}`;
            })
            .join(" ");
          return (
            <g key={s.label}>
              <polyline
                fill="none"
                stroke={s.color}
                strokeWidth="2"
                points={pts}
              />
              {s.values.map((v, i) => {
                const x = PAD.l + (n > 1 ? i * xStep : innerW / 2);
                const y = PAD.t + innerH * (1 - (top ? v / top : 0));
                return (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r={2}
                    fill={s.color}
                  />
                );
              })}
            </g>
          );
        })}
        <g fill="#6d6a63" fontSize="10" textAnchor="middle">
          <text x={PAD.l} y={H - 8}>
            d1
          </text>
          <text x={W - PAD.r} y={H - 8}>
            d{n}
          </text>
        </g>
      </svg>
      <div className="mt-2 text-[11px] text-ink-soft flex flex-wrap gap-3">
        {series.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ background: s.color }}
            />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}
